import { derived, type Readable } from 'svelte/store';
import {
  createQueries,
  createQuery,
  type QueryClient,
} from '$lib/api/tanstack-query';

import { assertExists } from '$lib/assert';
import type { AuthContext } from '$lib/auth';
import type { ResourceType } from '$lib/permissions';
import { ALL_PERMISSIONS } from '$lib/permissions';

import type { AppServiceClient } from '../client';
import { prefetchQuery } from '../prefetch';
import {
  type BaseQueryOptions,
  createQueryOptions,
  getAuthedQueryOptions,
  useAuthedQueryOptions,
} from '../query-options';
import { permissionKeys } from './query-key';

const DEFAULT_STALE_TIME_MS = 5000;

/** Request interface to create a new permissions query */
export interface CreatePermissionsQueryRequest {
  resourceType: ResourceType | undefined;
  resourceID: string | undefined;
}

/** Create a query for `checkPermissions`. */
export const createHasPermissionsQuery = (
  request: Readable<CreatePermissionsQueryRequest>
) => {
  const baseOptions = useAuthedQueryOptions('app');

  const queryOptions = derived(
    [baseOptions, request],
    ([$baseOptions, $request]) =>
      hasPermissionsQueryOptions($baseOptions, $request)
  );

  return createQuery(queryOptions);
};

/** Create multiple queries for a list of `checkPermissions`. */
export const createHasPermissionsQueries = (
  requests: Readable<CreatePermissionsQueryRequest[]>
) => {
  const baseOptions = useAuthedQueryOptions('app');

  const queriesOptions = derived(
    [baseOptions, requests],
    ([$baseOptions, $requests]) =>
      $requests.map(($request) =>
        hasPermissionsQueryOptions($baseOptions, $request)
      )
  );

  return createQueries({
    queries: queriesOptions,
  });
};

/** Prefetch `checkPermissions` data. */
export const prefetchHasPermissions = async (
  queryClient: QueryClient,
  client: AppServiceClient,
  authContext: AuthContext,
  request: CreatePermissionsQueryRequest
) => {
  const baseAuthedOptions = getAuthedQueryOptions(client, authContext);
  const options = hasPermissionsQueryOptions(baseAuthedOptions, request);

  return prefetchQuery(queryClient, options);
};

const hasPermissionsQueryOptions = (
  { client, getRPCOptions, enabled }: BaseQueryOptions<AppServiceClient>,
  request: CreatePermissionsQueryRequest
) => {
  return createQueryOptions({
    queryKey: permissionKeys.get(request.resourceType, request.resourceID),
    staleTime: DEFAULT_STALE_TIME_MS,
    enabled:
      enabled &&
      request.resourceType !== undefined &&
      request.resourceID !== undefined,
    queryFn: async () => {
      assertExists(request.resourceType, 'Missing resource type');
      assertExists(request.resourceID, 'Missing resource ID');

      const rpcOptions = await getRPCOptions();
      const { response } = await client.checkPermissions(
        {
          permissions: [
            {
              resourceType: request.resourceType,
              resourceId: request.resourceID,
              // check all permissions on a resource
              permissions: [...ALL_PERMISSIONS],
            },
          ],
        },
        rpcOptions
      );

      return response;
    },
  });
};
